import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const EMPTY = { username: '', password: '', email: '', nom: '', prenom: '', role: 'user', site_id: '', actif: true };

export default function Users({ currentUser, setHeaderAction }) {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { ...user } (id absent = création)
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get('/users'), api.get('/sites')]);
      setUsers(u.data);
      setSites(s.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const body = { ...modal, site_id: modal.site_id || null };
    try {
      if (modal.id) await api.put(`/users/${modal.id}`, body);
      else await api.post('/users', body);
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer ${u.username} ?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.nom || '—';
  const canDelete = currentUser.role === 'super-admin';

  const openNew = useCallback(() => { setError(''); setModal({ ...EMPTY }); }, []);
  useEffect(() => {
    if (!setHeaderAction) return undefined;
    setHeaderAction(<button className="page-action-btn" onClick={openNew}>+ Nouvel utilisateur</button>);
    return () => setHeaderAction(null);
  }, [openNew, setHeaderAction]);

  return (
    <div className="page-container">
      <div className="page-header-row">
        <div>
          <h2 className="page-title">Utilisateurs</h2>
          <p className="page-subtitle">{users.length} compte(s)</p>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr><th>Identifiant</th><th>Nom</th><th>Rôle</th><th>Site</th><th>Statut</th><th></th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="table-empty">Chargement…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="table-empty">Aucun utilisateur.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td>{[u.prenom, u.nom].filter(Boolean).join(' ') || '—'}</td>
                <td><span className="badge-pill badge-neutral">{u.role}</span></td>
                <td>{siteName(u.site_id)}</td>
                <td>{u.actif ? <span className="badge-pill badge-success">Actif</span> : <span className="badge-pill badge-error">Inactif</span>}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn-icon" title="Modifier" onClick={() => { setError(''); setModal({ ...u, password: '' }); }}>✏️</button>
                    {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => remove(u)}>🗑️</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal.id ? 'Modifier' : 'Nouvel utilisateur'}</h2>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={save}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Identifiant *</label>
                  <input className="form-control" value={modal.username} disabled={!!modal.id}
                    onChange={(e) => setModal({ ...modal, username: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{modal.id ? 'Nouveau mot de passe (laisser vide = inchangé)' : 'Mot de passe *'}</label>
                  <input type="password" className="form-control" value={modal.password}
                    onChange={(e) => setModal({ ...modal, password: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Prénom</label>
                    <input className="form-control" value={modal.prenom || ''} onChange={(e) => setModal({ ...modal, prenom: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Nom</label>
                    <input className="form-control" value={modal.nom || ''} onChange={(e) => setModal({ ...modal, nom: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" value={modal.email || ''} onChange={(e) => setModal({ ...modal, email: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Rôle</label>
                    <select className="form-control" value={modal.role} onChange={(e) => setModal({ ...modal, role: e.target.value })}>
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                      {currentUser.role === 'super-admin' && <option value="super-admin">super-admin</option>}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Site</label>
                    <select className="form-control" value={modal.site_id || ''} onChange={(e) => setModal({ ...modal, site_id: e.target.value })}>
                      <option value="">— Aucun</option>
                      {sites.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                    </select>
                  </div>
                </div>
                {modal.id && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input type="checkbox" checked={!!modal.actif} onChange={(e) => setModal({ ...modal, actif: e.target.checked })} />
                    Compte actif
                  </label>
                )}
                {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setModal(null)}>Annuler</button>
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
